import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { type Result } from '@vello/core';
import { type MeProfile, type OperatorLoginInput, type ApiError, type OperatingMarketView, type AdministrativeRegionView } from '@vello/api-client';
import { type SessionState, resolveBootRoute, createCrossContextLock, createSessionChangeSignal, createSharedActivity, logoutSession, refreshSession, shouldRefreshSession, signOutIfIdle, verifyStoredSession } from '@vello/app-shell';
import {
  type AdminRuntime,
  type OperatorScope,
  type ScopeState,
  bootAdmin,
  establishOperatorSession,
  switchScope,
  requiresReauth,
  scopesFromGrantsAndMarkets,
  preferredScope,
  scopeAllowed,
  landingRoute,
  screenAllowed,
  type KnownGrants,
} from '../src/index';
import { deviceFingerprint } from './device';
import { browserTimeZone, locateBrowser, rememberScope, rememberedScope } from './scope-locator';
import { PUBLIC_ROUTES, useRouter } from './router';

const CURRENT_VERSION = '1.0.0';
const INACTIVITY_MS = 15 * 60 * 1000; // 15 minutes idle → re-auth
const WATCHDOG_INTERVAL_MS = 30_000;
/** Renew the access token this long before it expires (> 2 watchdog ticks, so one failed attempt can retry). */
const REFRESH_AHEAD_SECONDS = 120;

export interface AdminContextValue {
  readonly runtime: AdminRuntime;
  readonly session: SessionState;
  readonly me: MeProfile | undefined;
  readonly scope: ScopeState;
  /**
   * The operator's own scoped grants (`GET /v1/operator/grants`) — the authority the console filters its menu,
   * its landing route and its market pickers by (D364). **`undefined` until that read answers, and again if it
   * fails** — an unknown authority is not an empty one, so the console filters nothing rather than claiming a
   * refusal the operator does not have (see `KnownGrants`). The server re-authorizes every call regardless.
   */
  readonly grants: KnownGrants;
  /** The registered operating-market catalog (m0101) — the scope selector's source of cities + their names. */
  readonly markets: readonly OperatingMarketView[];
  /**
   * STATE-SCOPE S2 (ADR-0124): the administrative-region (state) catalog - the selector's third level and the names of a
   * state. Optional: a context built without it (a fixed test context, an older server that has no regions route) reads as
   * "no states".
   */
  readonly regions?: readonly AdministrativeRegionView[];
  /**
   * ADR-0124 (the regions console): read the state catalog again after a manager renamed or retired one, so the names in the selector and every list, and the choices of
   * every state select, follow at once. Optional like `regions`: a fixed test context has nothing to refresh.
   */
  readonly refreshRegions?: () => Promise<void>;
  readonly booted: boolean;
  /**
   * D806 ("Meu perfil"): adopt the account after the operator changed their own name, language or photo, so the sidebar follows at once.
   * Optional like `regions`: a fixed test context has nothing to update.
   */
  readonly setMe?: (me: MeProfile) => void;
  readonly setScope: (target: OperatorScope) => void;
  readonly login: (input: OperatorLoginInput) => Promise<Result<SessionState, ApiError>>;
  readonly logout: () => Promise<void>;
}

/** Exported so rendered tests can mount a screen over a fixed operator context without the boot sequence (D358). */
export const AdminContext = createContext<AdminContextValue | undefined>(undefined);

function nowIso(): string {
  return new Date().toISOString();
}

/** Fallback scope when the grants read is unavailable: a global scope + the operator's own country. */
function seedScope(me: MeProfile | undefined): ScopeState {
  const allowed: OperatorScope[] = [{}];
  if (me?.countryCode !== undefined && me.countryCode !== '') allowed.push({ country: me.countryCode });
  return { current: {}, allowed };
}

/**
 * Load the operator's profile, grants and the market catalog together, then fuse grants (authority) with the catalog
 * (the cities inside it) into the scope selector (D358, `scopesFromGrantsAndMarkets`): a city scope carries the
 * canonical `cc:slug` RBAC compares, and its display name comes from the catalog. When the grants read fails, the
 * console falls back to the operator's own country (the server still authorizes every call).
 */
async function loadOperatorContext(runtime: AdminRuntime): Promise<{ profile: MeProfile; scope: ScopeState; markets: readonly OperatingMarketView[]; regions: readonly AdministrativeRegionView[]; grants: KnownGrants } | undefined> {
  const [profile, grants, mk, rg] = await Promise.all([runtime.auth.me(), runtime.operatorAuth.grants(), runtime.markets.listMarkets(), runtime.markets.regions()]);
  if (!profile.ok) return undefined;
  const markets = mk.ok ? mk.value.markets : [];
  // A failed regions read (or a server without the route) leaves no states: the selector then offers countries and cities only.
  const regions = rg.ok ? rg.value.regions : [];
  return {
    profile: profile.value,
    markets,
    regions,
    // D364: the grants are kept, not only folded into the scope selector — the menu, the landing route and the
    // market pickers are derived from them. A FAILED read leaves them `undefined`, NOT `[]`: an unknown authority
    // must not be filtered as if it were an empty one, or one transient failure of this single read would hide
    // the whole console from every persona. The server is the authority either way.
    grants: grants.ok ? grants.value.grants : undefined,
    scope: grants.ok ? scopesFromGrantsAndMarkets(grants.value.grants, markets, regions) : seedScope(profile.value),
  };
}

export function AdminProvider({ runtime, children }: { runtime: AdminRuntime; children: ReactNode }): ReactElement {
  const { navigate, route: entryRoute } = useRouter();
  // The route the address named when the console opened (a reload or a shared link): the boot keeps it instead of dropping to the dashboard.
  const entryRouteRef = useRef(entryRoute);
  const [session, setSession] = useState<SessionState>(() => runtime.sessionStore.snapshot());
  const [me, setMe] = useState<MeProfile | undefined>(undefined);
  const [scope, setScopeState] = useState<ScopeState>(() => seedScope(undefined));
  const [markets, setMarkets] = useState<readonly OperatingMarketView[]>([]);
  const [regions, setRegions] = useState<readonly AdministrativeRegionView[]>([]);
  const [grants, setGrants] = useState<KnownGrants>(undefined);
  const [booted, setBooted] = useState<boolean>(false);
  const lastActivityRef = useRef<string>(nowIso());
  // The starting scope (owner, 2026-10-12): the scope this operator last chose here; else where the browser is (the
  // near city, else its country — `preferredScope`); global only for a browser where no market exists. The time-zone
  // guess applies at once; the location (one browser prompt) upgrades it to a city when it answers — unless the
  // operator already switched the scope by hand.
  const scopeTouched = useRef(false);
  const startScope = useCallback((ctx: { profile: MeProfile; scope: ScopeState; markets: readonly OperatingMarketView[] }): void => {
    scopeTouched.current = false;
    const remembered = rememberedScope(ctx.profile.id);
    if (remembered !== undefined && scopeAllowed(ctx.scope, remembered)) {
      setScopeState({ ...ctx.scope, current: remembered });
      return;
    }
    const timeZone = browserTimeZone();
    setScopeState({ ...ctx.scope, current: preferredScope(ctx.scope, ctx.markets, timeZone !== undefined ? { timeZone } : {}) });
    if (!ctx.markets.some((m) => m.center !== undefined)) return;
    void locateBrowser().then((position) => {
      if (position === undefined || scopeTouched.current) return;
      setScopeState((s) => (scopeTouched.current ? s : { ...s, current: preferredScope(s, ctx.markets, { position, ...(timeZone !== undefined ? { timeZone } : {}) }) }));
    });
  }, []);
  // ADR-0039 (Codex r1 P1-1): one refresh at a time across every tab of this origin (Web Locks), and every tab adopts
  // another tab's rotation or sign-out from the shared session (BroadcastChannel / storage event — no token on the wire).
  const coordination = useMemo(() => ({ lock: createCrossContextLock(), signal: createSessionChangeSignal(), activity: createSharedActivity() }), []);
  useEffect(() => () => coordination.signal.close(), [coordination]);
  useEffect(() => {
    if (!booted) return undefined;
    return coordination.signal.subscribe(() => {
      if (runtime.sessionStore.snapshot().status !== 'authenticated') return; // only an active session adopts changes
      void runtime.sessionStore.load().then((snap) => {
        setSession(snap);
        if (snap.status !== 'authenticated') {
          setMe(undefined);
          navigate('session_expired');
        }
      });
    });
  }, [booted, runtime, navigate, coordination]);

  // Boot once: resolve the initial route from the persisted session, and load the operator profile if signed in.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const boot = await bootAdmin(runtime, {
        currentVersion: CURRENT_VERSION,
        updatePolicy: { minSupported: CURRENT_VERSION },
        network: navigator.onLine ? 'online' : 'offline',
      });
      if (cancelled) return;
      let snap = runtime.sessionStore.snapshot();
      setSession(snap);
      if (snap.status === 'authenticated') {
        // D849: a session left in storage may be dead (revoked, expired): ask once, renew through the device-bound refresh, and when the
        // server still refuses clear it and show the clean "your session expired" screen - never a half-working console.
        const verdict = await verifyStoredSession({
          sessionStore: runtime.sessionStore,
          auth: runtime.auth,
          deviceFingerprint: await deviceFingerprint(),
          probe: () => runtime.auth.me(),
          lock: coordination.lock,
          signal: coordination.signal,
        });
        if (cancelled) return;
        if (verdict.kind === 'expired') {
          setSession(runtime.sessionStore.snapshot());
          setMe(undefined);
          setBooted(true);
          navigate('session_expired');
          return;
        }
        snap = runtime.sessionStore.snapshot();
        setSession(snap);
      }
      if (snap.status === 'authenticated') {
        const ctx = await loadOperatorContext(runtime);
        if (cancelled) return;
        if (ctx !== undefined) {
          setMe(ctx.profile);
          setMarkets(ctx.markets);
          setRegions(ctx.regions);
          startScope(ctx);
          setGrants(ctx.grants);
          // D364: a boot that resolved the default route must not drop this persona on a screen its grant
          // refuses (an atendente used to land on the ops Overview → 403). D854 (L7a-001): a reload or a shared link
          // keeps its screen and its filters (the address is left untouched); that screen shows its own "no
          // permission" state when the grant refuses it.
          setBooted(true);
          const entry = entryRouteRef.current;
          const next = resolveBootRoute({ bootRoute: boot.route, home: 'dashboard', current: entry, authenticated: true, publicRoutes: PUBLIC_ROUTES });
          const destination = next ?? (entry === 'dashboard' ? 'dashboard' : undefined);
          if (destination === 'dashboard' && !screenAllowed(ctx.grants, 'dashboard')) navigate(landingRoute(ctx.grants));
          else if (next !== undefined) navigate(next);
          return;
        }
      }
      setBooted(true);
      const next = resolveBootRoute({ bootRoute: boot.route, home: 'dashboard', current: entryRouteRef.current, authenticated: snap.status === 'authenticated', publicRoutes: PUBLIC_ROUTES });
      if (next !== undefined) navigate(next);
    })();
    return () => {
      cancelled = true;
    };
    // Boot must run exactly once; `runtime` and `navigate` are stable for the app lifetime.
  }, [runtime, navigate]);

  // Inactivity / expiry watchdog: force re-auth when idle or the token has expired.
  useEffect(() => {
    if (!booted) return;
    const markActivity = (): void => {
      lastActivityRef.current = nowIso();
      coordination.activity.mark(lastActivityRef.current); // shared: an idle tab must not sign out an active one
    };
    window.addEventListener('pointerdown', markActivity);
    window.addEventListener('keydown', markActivity);
    let refreshing = false;
    const timer = window.setInterval(() => {
      if (runtime.sessionStore.snapshot().status !== 'authenticated' || refreshing) return;
      const now = nowIso();
      // Codex r2 P1-3: inactivity is the latest activity of ANY tab of this origin (shared), not only this tab.
      const lastActivityAt = coordination.activity.latest(lastActivityRef.current);
      // ADR-0039: while the operator is active, renew the short-lived access token through the device-bound refresh
      // (the runtime attaches the device-key proof) instead of forcing a credential+MFA re-login at every expiry.
      if (shouldRefreshSession({ sessionStore: runtime.sessionStore, now, lastActivityAt, inactivityMs: INACTIVITY_MS, aheadSeconds: REFRESH_AHEAD_SECONDS })) {
        refreshing = true;
        void deviceFingerprint()
          .then((fingerprint) => refreshSession({ sessionStore: runtime.sessionStore, auth: runtime.auth, deviceFingerprint: fingerprint, lock: coordination.lock, signal: coordination.signal }))
          .then(() => {
            const snap = runtime.sessionStore.snapshot();
            setSession(snap);
            if (snap.status !== 'authenticated') {
              setMe(undefined);
              navigate('session_expired');
            }
          })
          .finally(() => {
            refreshing = false;
          });
        return;
      }
      const mustReauth = requiresReauth({
        sessionStore: runtime.sessionStore,
        now,
        lastActivityAt,
        inactivityMs: INACTIVITY_MS,
      });
      if (mustReauth) {
        // Codex r2 P1-3/P1-4: re-check the SHARED session + activity under the session lock before ending anything; a real
        // end revokes the server session and tombstones the shared session for every tab.
        refreshing = true;
        void signOutIfIdle({ sessionStore: runtime.sessionStore, auth: runtime.auth, deviceFingerprint, lock: coordination.lock, signal: coordination.signal, activity: coordination.activity, localActivityAt: lastActivityRef.current, now, inactivityMs: INACTIVITY_MS })
          .then((outcome) => {
            setSession(runtime.sessionStore.snapshot());
            if (outcome !== 'kept') {
              setMe(undefined);
              navigate('session_expired');
            }
          })
          .finally(() => {
            refreshing = false;
          });
      }
    }, WATCHDOG_INTERVAL_MS);
    return () => {
      window.removeEventListener('pointerdown', markActivity);
      window.removeEventListener('keydown', markActivity);
      window.clearInterval(timer);
    };
  }, [booted, runtime, navigate, coordination]);

  const value = useMemo<AdminContextValue>(() => {
    return {
      runtime,
      session,
      me,
      scope,
      grants,
      markets,
      regions,
      refreshRegions: async () => {
        const res = await runtime.markets.regions();
        if (res.ok) setRegions(res.value.regions);
      },
      booted,
      setMe,
      setScope: (target) => {
        scopeTouched.current = true;
        if (me !== undefined) rememberScope(me.id, target);
        setScopeState((s) => switchScope(s, target));
      },
      login: async (input) => {
        const result = await establishOperatorSession(runtime, input, { lock: coordination.lock, signal: coordination.signal });
        if (result.ok) {
          setSession(result.value);
          lastActivityRef.current = nowIso();
          const ctx = await loadOperatorContext(runtime);
          if (ctx !== undefined) {
            setMe(ctx.profile);
            setMarkets(ctx.markets);
            setRegions(ctx.regions);
            startScope(ctx);
            setGrants(ctx.grants);
          }
          // D364: land on the first screen this operator's grants actually open, not always on Overview.
          navigate(landingRoute(ctx?.grants));
        }
        return result;
      },
      logout: async () => {
        // Codex r2/r3 P1-4/P1-1: under the session lock — tombstone first, revoke server-side with the refresh credential (works
        // after access-token expiry), clear only this session, and signal every tab.
        await logoutSession({ sessionStore: runtime.sessionStore, auth: runtime.auth, deviceFingerprint, lock: coordination.lock, signal: coordination.signal });
        setSession(runtime.sessionStore.snapshot());
        setMe(undefined);
        setGrants(undefined);
        navigate('login');
      },
    };
  }, [runtime, session, me, scope, grants, markets, regions, booted, navigate, startScope]);

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
}

export function useAdmin(): AdminContextValue {
  const value = useContext(AdminContext);
  if (value === undefined) throw new Error('useAdmin must be used within an AdminProvider');
  return value;
}
